import { useMemo, useSyncExternalStore } from 'react'
import {
  emptyTriage,
  readTriage,
  signalLocalId,
  type Disposition,
  type PinnedRow,
  type SignalStatus,
  type SignalTriage,
} from './signal-model'

/**
 * Signal triage persistence — localStorage v1 (decision S4), behind an
 * interface the future signals API implements without the UI noticing.
 *
 * THIS INTERFACE IS THE DRAFT API CONTRACT for needs-API ledger item 1
 * (docs/signals-requirements.md §7). Each method becomes an endpoint and
 * LocalTriageStore retires.
 *
 * Storage: ONE key per cluster+scope, `bloo.signal-triage.<clusterId>.<scopeId>`,
 * holding a JSON array of SignalTriage. Records are created lazily on the
 * first touch, so an untouched signal costs nothing. House localStorage
 * rules (cf. hunt-store): try-catch everywhere, SSR-safe, corrupt records
 * quarantined by the tolerant reader.
 */

export interface TriageStore {
  /** All records — a stable snapshot until the next mutation. */
  all: () => Map<string, SignalTriage>
  get: (uuid: string) => SignalTriage | null

  setStatus: (uuid: string, status: SignalStatus, who: string) => void
  /** Closes with a disposition; many uuids at once for bulk close. */
  close: (
    uuids: string[],
    input: { disposition: Disposition; dispositionType?: string | null; note: string },
    who: string,
  ) => void
  reopen: (uuid: string, who: string) => void

  /** `label` names the case in the activity log (`case-N`); the triage keeps the id. */
  linkCase: (uuids: string[], caseId: string, who: string, label: string) => void
  linkHunt: (uuid: string, huntId: string, who: string) => void

  addNote: (uuid: string, text: string, who: string) => void
  pin: (uuid: string, pin: Omit<PinnedRow, 'id' | 'at'>, who: string) => void
  unpin: (uuid: string, pinId: string) => void
  /** Free-form audit entry (e.g. "suppression rule created"). */
  log: (uuid: string, what: string, who: string) => void

  /** True when the last write was refused for space. */
  quotaWarning: () => string | null
  subscribe: (cb: () => void) => () => void
}

export function triageStorageKey(clusterId: string, scopeId: string): string {
  return `bloo.signal-triage.${clusterId}.${scopeId}`
}

export const PIN_VALUE_MAX = 500
export const PIN_FIELDS_MAX = 12
export const STORE_SOFT_BUDGET_BYTES = 1_500_000

export function trimPin(excerpt: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  let n = 0
  for (const [k, v] of Object.entries(excerpt)) {
    if (n >= PIN_FIELDS_MAX) break
    out[k] = v.length > PIN_VALUE_MAX ? `${v.slice(0, PIN_VALUE_MAX)}…` : v
    n++
  }
  return out
}

const stores = new Map<string, LocalTriageStore>()

export function localTriageStore(clusterId: string, scopeId: string): TriageStore {
  const key = triageStorageKey(clusterId, scopeId)
  let s = stores.get(key)
  if (!s) {
    s = new LocalTriageStore(key)
    stores.set(key, s)
  }
  return s
}

class LocalTriageStore implements TriageStore {
  private key: string
  private cache: Map<string, SignalTriage> | null = null
  private warning: string | null = null
  private listeners = new Set<() => void>()

  constructor(key: string) {
    this.key = key
  }

  private read(): Map<string, SignalTriage> {
    if (this.cache) return this.cache
    let parsed: unknown
    try {
      const raw = globalThis.localStorage?.getItem(this.key)
      parsed = raw ? JSON.parse(raw) : []
    } catch {
      parsed = []
    }
    const map = new Map<string, SignalTriage>()
    for (const v of Array.isArray(parsed) ? parsed : []) {
      const t = readTriage(v)
      if (t) map.set(t.uuid, t)
    }
    this.cache = map
    return map
  }

  private write(next: Map<string, SignalTriage>, opts?: { bulky?: boolean }): boolean {
    const body = JSON.stringify([...next.values()])
    if (opts?.bulky && body.length > STORE_SOFT_BUDGET_BYTES) {
      this.warning = 'signal triage storage is near its browser quota — unpin heavy rows or clear closed signals'
      this.notify()
      return false
    }
    try {
      globalThis.localStorage?.setItem(this.key, body)
      this.cache = next
      this.warning = null
      this.notify()
      return true
    } catch {
      this.warning = 'the browser refused to save — signal triage storage is full'
      this.notify()
      return false
    }
  }

  /** Touch = create-if-missing, apply, stamp, append log. */
  private mutate(
    uuids: string[],
    fn: (t: SignalTriage) => SignalTriage,
    entry: { who: string; what: string } | null,
    opts?: { bulky?: boolean },
  ): void {
    const cur = this.read()
    const next = new Map(cur)
    const now = Date.now()
    for (const uuid of uuids) {
      const before = cur.get(uuid) ?? emptyTriage(uuid)
      const after = fn(before)
      next.set(uuid, {
        ...after,
        updatedAt: now,
        log: entry ? [...after.log, { at: now, who: entry.who, what: entry.what }] : after.log,
      })
    }
    this.write(next, opts)
  }

  private notify() {
    for (const cb of this.listeners) cb()
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => {
      this.listeners.delete(cb)
    }
  }

  quotaWarning = () => this.warning
  all = () => this.read()
  get = (uuid: string) => this.read().get(uuid) ?? null

  setStatus = (uuid: string, status: SignalStatus, who: string) => {
    this.mutate([uuid], (t) => ({ ...t, status, closedAt: status === 'closed' ? t.closedAt ?? Date.now() : null }), {
      who,
      what: `status ${status.replace('_', ' ')}`,
    })
  }

  close = (
    uuids: string[],
    input: { disposition: Disposition; dispositionType?: string | null; note: string },
    who: string,
  ) => {
    this.mutate(
      uuids,
      (t) => ({
        ...t,
        status: 'closed',
        disposition: input.disposition,
        dispositionType: input.dispositionType ?? null,
        note: input.note,
        closedAt: Date.now(),
      }),
      { who, what: `closed as ${input.disposition.replace(/_/g, ' ')}${input.dispositionType ? ` (${input.dispositionType})` : ''}` },
    )
  }

  reopen = (uuid: string, who: string) => {
    this.mutate([uuid], (t) => ({ ...t, status: 'in_progress', closedAt: null }), { who, what: 'reopened' })
  }

  linkCase = (uuids: string[], caseId: string, who: string, label: string) => {
    this.mutate(
      uuids,
      (t) => ({ ...t, caseId, status: t.status === 'open' ? 'in_progress' : t.status }),
      { who, what: `added to case ${label}` },
    )
  }

  linkHunt = (uuid: string, huntId: string, who: string) => {
    this.mutate(
      [uuid],
      (t) => ({ ...t, huntId, status: t.status === 'open' ? 'in_progress' : t.status }),
      { who, what: 'opened as hunt' },
    )
  }

  addNote = (uuid: string, text: string, who: string) => {
    const clean = text.trim()
    if (!clean) return
    this.mutate(
      [uuid],
      (t) => ({ ...t, notes: [...t.notes, { id: signalLocalId('n'), at: Date.now(), who, text: clean }] }),
      { who, what: 'note added' },
      { bulky: true },
    )
  }

  pin = (uuid: string, pin: Omit<PinnedRow, 'id' | 'at'>, who: string) => {
    this.mutate(
      [uuid],
      (t) => ({
        ...t,
        pins: [...t.pins, { id: signalLocalId('p'), at: Date.now(), excerpt: trimPin(pin.excerpt), sourceQuery: pin.sourceQuery }],
      }),
      { who, what: 'pinned a raw event' },
      { bulky: true },
    )
  }

  unpin = (uuid: string, pinId: string) => {
    this.mutate([uuid], (t) => ({ ...t, pins: t.pins.filter((p) => p.id !== pinId) }), null)
  }

  log = (uuid: string, what: string, who: string) => {
    this.mutate([uuid], (t) => t, { who, what })
  }
}

/* ----------------------------------------------------------------- React */

/** Live triage map for a scope — re-renders on every store mutation. */
export function useTriage(clusterId: string, scopeId: string): { triage: Map<string, SignalTriage>; store: TriageStore } {
  const store = localTriageStore(clusterId, scopeId)
  const triage = useSyncExternalStore(store.subscribe, store.all, store.all)
  return useMemo(() => ({ triage, store }), [triage, store])
}
